import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { MessagesSquare , ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/atoms/Card";
import { Button } from "@/components/ui/atoms/Button";
import { useChatSession } from "@/hooks/useChatSession";
import type { ChatSessionMessage } from "@/hooks/useChatSession";

import { ChatHeader } from "./ChatHeader";
import { ChatInputBar } from "./ChatInputBar";
import { DateSeparator } from "./DateSeparator";
import { MessageGroup } from "./MessageGroup";
import { SuggestionPill } from "./SuggestionPill";
import { IconWrapper } from "../ui/atoms";

interface ChatbotWidgetProps {
  apiUrl?: string;
}

interface ChatMessageGroup {
  key: string;
  role: "user" | "bot";
  messages: ChatSessionMessage[];
  timestamp: Date;
}

/** Live snapshot of the visible portion of the screen (shrinks when keyboard opens). */
interface VVSnapshot {
  offsetTop: number;
  height: number;
  width: number;
}

const GROUP_GAP_MS = 60_000;

/**
 * Pages where the chatbot must NOT appear:
 * candidate-facing flows, auth/system pages, payment, admin, onboarding.
 * Everything else gets the widget.
 */
function isChatbotBlocked(pathname: string): boolean {
  return (
    // Public talent landing: one conversion path (book a call), no assistant.
    pathname === "/" ||
    pathname === "/callback" ||
    pathname === "/suspended" ||
    pathname === "/notauthorized" ||
    pathname === "/admin" ||
    pathname.startsWith("/invite/") ||
    pathname.startsWith("/interview/") ||
    pathname.startsWith("/actual-interview/") ||
    pathname.startsWith("/onboarding/") ||
    pathname.startsWith("/payment/") ||
    pathname.startsWith("/gitex-challenge/cv/") ||
    // /job/:id/cv — candidate CV upload, not the recruiter job details page
    /^\/job\/[^/]+\/cv$/.test(pathname)
  );
}
const MOBILE_MAX_W = 768;
const PANEL_SIDE_INSET = 12;

type PageContext = "landing" | "app";

/**
 * App context = authenticated workspace pages. Landing = public pages.
 * Blocked routes (interview, onboarding, etc.) never reach here.
 */
function getPageContext(pathname: string): PageContext {
  if (
    pathname === "/jobs" ||
    pathname.startsWith("/job/") ||
    pathname.startsWith("/jobs/") ||
    pathname === "/integrations" ||
    pathname.startsWith("/integrations/")
  ) {
    return "app";
  }
  return "landing";
}

function getWelcomeMessages(
  context: PageContext,
  t: TFunction<"chatbot">,
): ChatSessionMessage[] {
  const followUp =
    context === "app" ? t("welcome.followup_app") : t("welcome.followup_landing");
  const now = new Date();
  return [
    {
      id: "welcome-greeting",
      role: "bot",
      content: t("welcome.greeting"),
      isError: false,
      timestamp: now,
    },
    {
      id: "welcome-followup",
      role: "bot",
      content: followUp,
      isError: false,
      timestamp: now,
    },
  ];
}

/**
 * Suggestion pills shown above the input. Two clean sets — one for landing
 * (someone exploring the product) and one for the app (a logged-in user
 * mid-workflow). Inside the app, we refine further per page so the
 * suggestions match what the user is likely looking at.
 */
function getSuggestions(
  context: PageContext,
  pathname: string,
  t: TFunction<"chatbot">,
): string[] {
  const read = (key: string): string[] =>
    (t as unknown as (k: string, o: { returnObjects: true }) => string[])(key, {
      returnObjects: true,
    });

  if (context === "landing") {
    return read("suggestions.landing");
  }

  // App context — bias toward the page the user is on.
  if (pathname.startsWith("/job/") || pathname.startsWith("/jobs/")) {
    return read("suggestions.jobs");
  }
  if (pathname.startsWith("/integrations")) {
    return read("suggestions.integrations");
  }
  // Default app suggestions — covers /dashboard and any other in-app page.
  return read("suggestions.default");
}

function readVV(): VVSnapshot {
  const vv = window.visualViewport;
  return vv
    ? { offsetTop: vv.offsetTop, height: vv.height, width: vv.width }
    : { offsetTop: 0, height: window.innerHeight, width: window.innerWidth };
}

export function ChatbotWidget({ apiUrl }: ChatbotWidgetProps) {
  const location = useLocation();
  const { t } = useTranslation("chatbot");

  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(`(max-width: ${MOBILE_MAX_W}px)`).matches
  );
  const [isMobileFullscreen, setIsMobileFullscreen] = useState(false);
  const [vv, setVv] = useState<VVSnapshot>(readVV);

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const fabRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isOpenRef = useRef(isOpen);

  const locationRef = useRef(location);
  locationRef.current = location;
  const { messages, isTyping, sendMessage: sendChatMessage, clearMessages } =
    useChatSession(apiUrl, () => ({
      page_context: getPageContext(locationRef.current.pathname),
      page_path: locationRef.current.pathname,
    }));

  // Keep isOpenRef current; reset fullscreen when chat closes
  useEffect(() => {
    isOpenRef.current = isOpen;
    if (!isOpen) setIsMobileFullscreen(false);
  }, [isOpen]);

  // Track visualViewport (keyboard open/close, scroll)
  useEffect(() => {
    const sync = () => setVv(readVV());
    const vp = window.visualViewport;
    vp?.addEventListener("resize", sync);
    vp?.addEventListener("scroll", sync);
    window.addEventListener("resize", sync);
    sync();
    return () => {
      vp?.removeEventListener("resize", sync);
      vp?.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
    };
  }, []);

  // Watch mobile breakpoint
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_W}px)`);
    const sync = () => setIsMobile(mq.matches);
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // Outside-click closes panel (disabled in fullscreen — header X handles it)
  useEffect(() => {
    if (!isOpen || (isMobile && isMobileFullscreen)) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t || panelRef.current?.contains(t) || fabRef.current?.contains(t)) return;
      setIsOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [isOpen, isMobile, isMobileFullscreen]);

  // Scroll body to bottom on new messages / state changes
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, isOpen, isTyping, isMobileFullscreen, vv.height]);

  const groupedMessages = useMemo(() => {
    return messages.reduce<ChatMessageGroup[]>((groups, msg, i) => {
      const prev = messages[i - 1];
      const sameRole = prev?.role === msg.role;
      const withinGap =
        prev &&
        new Date(msg.timestamp).getTime() - new Date(prev.timestamp).getTime() <= GROUP_GAP_MS;

      if (!sameRole || !withinGap) {
        groups.push({ key: msg.id, role: msg.role, messages: [msg], timestamp: new Date(msg.timestamp) });
        return groups;
      }

      const last = groups[groups.length - 1];
      last.messages = [...last.messages, msg];
      last.timestamp = new Date(msg.timestamp);
      return groups;
    }, []);
  }, [messages]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isTyping) return;
    setInput("");
    await sendChatMessage(trimmed);
    // Refocus input on mobile so keyboard stays open
    if (isMobile) requestAnimationFrame(() => inputRef.current?.focus());
  };

  // Derived layout flags
  const isFullscreen = isMobile && isOpen && isMobileFullscreen;
  // keyboard inset = pixels of screen hidden by the software keyboard
  const keyboardInset = window.innerHeight - vv.height - vv.offsetTop;

  const mobilePanelStyle =
    isMobile && isOpen
      ? isFullscreen
        ? { top: vv.offsetTop, left: 0, right: 0, height: vv.height, bottom: "auto" }
        : {
            left: PANEL_SIDE_INSET,
            right: PANEL_SIDE_INSET,
            maxHeight: Math.round(vv.height * 0.85),
            minHeight: Math.min(420, vv.height - 40),
            top: "auto",
          }
      : undefined;

  const panelClasses = [
    "fixed h-[32rem] transition-[opacity,transform] duration-300 ease-out",
    isOpen ? "pointer-events-auto scale-100 opacity-100" : "pointer-events-none scale-95 opacity-0",
    isFullscreen ? "z-[100]" : "z-[55]",
    !isMobile
      ? "bottom-20 right-3 w-[calc(100vw-1.5rem)] origin-bottom-right sm:right-5 sm:w-[27.5rem]"
      : "bottom-19",
  ].join(" ");

  // Hooks above must run on every route to keep hook order stable; only gate
  // the rendered output here, after all hooks have been called.
  if (isChatbotBlocked(location.pathname)) return null;

  return (
    <div className="fixed right-0 z-50">
      {/* Panel */}
      <div ref={panelRef} className={panelClasses} style={mobilePanelStyle}>
        <Card
          variant="custom"
          padding="none"
          rounded={isFullscreen ? "none" : "xl"}
          className="!m-0 flex h-full min-h-0 flex-col overflow-hidden border border-dark-invirtus-soft bg-light shadow-xl"
        >
          <ChatHeader
            onRefresh={clearMessages}
            onClose={() => setIsOpen(false)}
            mobileSnapToggle={
              isMobile && isOpen
                ? { isExpanded: isMobileFullscreen, onToggle: () => setIsMobileFullscreen((v) => !v) }
                : undefined
            }
          />

          <div
            ref={bodyRef}
            className="min-h-0 flex-1 overflow-auto bg-light [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-dark-invirtus-soft"
          >
            <div className="min-h-full p-4">
              <div className="flex flex-col gap-3">
                {/* Welcome bubbles — always rendered at the top of the conversation.
                    They stay visible as the user scrolls back up. No timestamp:
                    the welcome is timeless framing, not a real message. */}
                <MessageGroup
                  role="bot"
                  showAvatar
                  showTimestamp={false}
                  timestamp={new Date()}
                  messages={getWelcomeMessages(getPageContext(location.pathname), t)}
                />

                {/* Suggestion pills — only shown until the user sends their first
                    message. After that they vanish, like real conversational starters. */}
                {messages.length === 0 && (
                  <div className="mt-1 flex flex-col gap-2 pl-10">
                    {getSuggestions(getPageContext(location.pathname), location.pathname, t).map((s) => (
                      <SuggestionPill
                        key={s}
                        label={s}
                        onClick={() => {
                          if (!isOpen) setIsOpen(true);
                          void send(s);
                        }}
                      />
                    ))}
                  </div>
                )}

                {messages.length > 0 && (
                  <>
                    <DateSeparator label={t("date.today")} />
                    {groupedMessages.map((g) => (
                      <MessageGroup
                        key={g.key}
                        messages={g.messages}
                        role={g.role}
                        showAvatar={g.role === "bot"}
                        timestamp={g.timestamp}
                      />
                    ))}
                  </>
                )}

                {isTyping && (
                  <MessageGroup messages={[]} role="bot" showAvatar timestamp={new Date()} showTypingIndicator />
                )}
              </div>
            </div>
          </div>

          <ChatInputBar
            value={input}
            disabled={!isMobile && isTyping}
            onChange={setInput}
            onSubmit={() => void send(input)}
            inputRef={inputRef}
          />
        </Card>
      </div>

      {/* FAB — hidden in fullscreen (header X takes over) */}
      {!isFullscreen && (
        <div
          ref={fabRef}
          className="pointer-events-auto fixed right-3 z-[51] flex flex-col items-end gap-2 sm:right-5"
          style={{ bottom: keyboardInset > 0 ? keyboardInset + 16 : "1rem" }}
        >

          {!isOpen ? (
            <Button
              type="outlined"
              intent="primary"
              size="md"
              aria-label={t("fab.open")}
              onClick={() => setIsOpen(true)}
              className="relative h-12 w-12 min-h-12 min-w-12 sm:h-14 sm:w-14 sm:min-h-14 sm:min-w-14 rounded-full overflow-visible !bg-light !border-purple !text-purple shadow-md hover:!bg-light-lin focus:!ring-purple/30"
            >
              <span className="grid place-items-center">
                <IconWrapper size="sm" color="primary"><MessagesSquare /></IconWrapper>
              </span>
            </Button>
          ) : (
            <Button
              type="icon"
              intent="primary"
              size="lg"
              icon={<ChevronDown strokeWidth={2.75} aria-hidden/>}
              aria-label={t("fab.close")}
              onClick={() => setIsOpen(false)}
              className="h-12 w-12 shrink-0 border border-dark-invirtus-soft !bg-light !text-purple shadow-md hover:!bg-light-lin focus:!ring-purple/30"
            />
          )}
        </div>
      )}
    </div>
  );
}
